import React, { useCallback, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

import type { IconName } from '../../assets/Icon/Icon';
import type { VariantProp } from '../../common/colors';
import type { ControlCondense } from '../../controls/shared/types';
import type { PolymorphicComponentProps } from '../../utilities/types/polymorphicAsProp';
import { Icon } from '../../assets/Icon/Icon';
import { fade, palette } from '../../common/colors';
import { atContainers } from '../../common/containers';
import { devices } from '../../common/devices';
import {
  getVariantStyles,
  useVariant,
  variantTokenBgColor,
  variantTokenIconColor,
  variantTokenLabelColor,
} from '../../common/variant';
import { useControlCondense, useControlSize } from '../../controls/shared/context';
import { colors, darkThemeSelector, styled } from '../../stitches.config';
import { BodySansSizes } from '../../text/Body';
import { CaptionSansSizes } from '../../text/Caption';
import { SmallSansSizes } from '../../text/Small';
import { Text } from '../../text/Text';
import { AlignStack, AlignStackElements } from '../../utilities/AlignStack/AlignStack';
import { resolveVariant } from '../../utilities/usePresentation';
import { useSelected } from '../../utilities/useSelected';
import { Tooltip } from '../Tooltip/Tooltip';
import { useBadgeArrangement, useBadgeEnds } from './BadgeContext';

export type BadgeArrangement = 'hidden-label' | 'leading-icon' | 'leading-label';
export type BadgeEnds = 'card' | 'pill';
export type BadgeSize = 'x-small' | 'small' | 'medium' | 'large';
export type BadgeVariant = VariantProp;

export type BadgeProps = {
  /**
   * Set the content's order and visibilty.
   */
  arrangement?: BadgeArrangement;
  /**
   * Displayed as the label (alternative to `label`) of the component.
   */
  children?: React.ReactNode;
  /**
   * Hides the label while a container query (`containers.*`) matches, keeping it in a tooltip. Pass
   * queries directly or via `BadgeGroup`; `true` uses the default (application below desktop
   * width). Only applies when there's an `icon` to fall back to.
   */
  condense?: boolean | ControlCondense;
  /**
   * Boolean to disable the component.
   */
  disabled?: boolean;
  /**
   * Whether to elide the label if it overflows (truncate with ellipses).
   */
  elide?: boolean;
  /**
   * Style choice for the left and right edges of the component.
   */
  ends?: BadgeEnds;
  /**
   * Set which icon to display, no value displays no icon.
   */
  icon?: IconName;
  /**
   * Set a unique identifier for the badge.
   */
  id?: string;
  /**
   * Boolean to show internal-only styles.
   */
  internal?: boolean;
  /**
   * Displayed as the label (alternative to `children`) of the component.
   */
  label?: React.ReactNode;
  /**
   * Event handler for clicking on a badge.
   */
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  /**
   * Set a size of the component.
   */
  size?: BadgeSize;
  /**
   * Set the most appropriate variant of the component for your use.
   */
  variant?: VariantProp;
  /**
   * Set a width specifically for the badge.
   */
  width?: string;
  /**
   * Override the tooltip contents when the badge shows a tooltip.
   */
  tooltipContents?: React.ReactNode;
  // Routing
  ref?: React.Ref<HTMLDivElement | null>;
};

export const BadgeIcon = styled(Icon, {
  color: variantTokenIconColor,
  display: 'flex',

  variants: {
    size: {
      'x-small': {
        widthAll: '$12',
        heightAll: '$12',
      },
      small: {
        widthAll: '$12',
        heightAll: '$12',
      },
      medium: {
        widthAll: '$14',
        heightAll: '$14',
      },
      large: {
        widthAll: '$16',
        heightAll: '$16',
      },
    },
  },
});
BadgeIcon.displayName = 'BadgeIcon';

const BadgeLabel = styled(Text, {
  maxWidth: '100%',
  color: variantTokenLabelColor,

  variants: {
    size: {
      'x-small': {
        ...CaptionSansSizes,
      },
      small: {
        ...SmallSansSizes,
      },
      medium: {
        ...BodySansSizes,
      },
      large: {
        ...BodySansSizes,
      },
    },
    elide: {
      false: {},
      true: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
    },
  },
});
BadgeLabel.displayName = 'BadgeLabel';

export const BadgeContainer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 'fit-content',

  backgroundColor: variantTokenBgColor,

  variants: {
    elide: {
      false: {
        flexShrink: 0,
      },
      true: {
        flexShrink: 1,
        maxWidth: '100%',
      },
    },
    clickable: {
      true: {
        cursor: 'pointer',
        userSelect: 'none',
      },
      false: {},
    },
    disabled: {
      true: {
        backgroundColor: fade(palette.tokenBgNeutralLight, 0.25),
        $$labelColor: colors.gray200,
        $$iconColor: colors.gray100,

        [darkThemeSelector]: {
          backgroundColor: fade(palette.tokenBgNeutralDark, 0.25),
          $$labelColor: colors.white,
          $$iconColor: colors.gray50,
        },
      },
      false: {},
    },
    ends: {
      card: {},
      pill: {
        borderRadius: 9999,
      },
    },
    size: {
      'x-small': {
        [devices.touch]: {
          minWidth: '$24',
          heightAll: '$20',
        },

        [devices.mouse]: {
          minWidth: '$20',
          heightAll: '$16',
          paddingY: '$2',
        },
      },
      small: {
        paddingY: '$2',

        [devices.touch]: {
          minWidth: '$28',
          heightAll: '$24',
        },

        [devices.mouse]: {
          minWidth: '$24',
          heightAll: '$20',
        },
      },
      medium: {
        paddingY: '$2',

        [devices.touch]: {
          minWidth: '$32',
          heightAll: '$28',
        },

        [devices.mouse]: {
          minWidth: '$28',
          heightAll: '$24',
        },
      },
      large: {
        paddingY: '$4',

        [devices.touch]: {
          minWidth: '$36',
          heightAll: '$32',
        },

        [devices.mouse]: {
          minWidth: '$32',
          heightAll: '$28',
        },
      },
    },
    variant: {
      alternative: {
        ...getVariantStyles('alternative'),
      },
      brand: {
        ...getVariantStyles('brand'),
      },
      attention: {
        ...getVariantStyles('attention'),
      },
      negative: {
        ...getVariantStyles('negative'),
      },
      neutral: {
        ...getVariantStyles('neutral'),
      },
      positive: {
        ...getVariantStyles('positive'),
      },
    },
    internal: {
      true: {},
      false: {},
    },
  },

  compoundVariants: [
    {
      ends: 'card',
      size: 'x-small',
      css: {
        paddingX: '$2',
        borderRadius: '$4',
      },
    },
    {
      ends: 'card',
      size: 'small',
      css: {
        paddingX: '$4',
        borderRadius: '$6',
      },
    },
    {
      ends: 'card',
      size: 'medium',
      css: {
        paddingX: '$6',
        borderRadius: '$8',
      },
    },
    {
      ends: 'card',
      size: 'large',
      css: {
        paddingX: '$8',
        borderRadius: '$10',
      },
    },
    {
      ends: 'pill',
      size: 'x-small',
      css: {
        paddingX: '$4',
      },
    },
    {
      ends: 'pill',
      size: 'small',
      css: {
        paddingX: '$6',
      },
    },
    {
      ends: 'pill',
      size: 'medium',
      css: {
        paddingX: '$8',
      },
    },
    {
      ends: 'pill',
      size: 'large',
      css: {
        paddingX: '$10',
      },
    },
    {
      internal: true,
      css: {
        ...getVariantStyles('neutral', true),
      },
    },
  ],
});
BadgeContainer.displayName = 'BadgeContainer';

const getBadgePreset = (size: BadgeSize) => {
  if (size === 'x-small') return 'caption';
  if (size === 'small') return 'small';
  return 'body';
};

export function Badge<Tag extends React.ElementType>({
  arrangement,
  children,
  condense,
  elide = true,
  ends,
  icon,
  id,
  internal,
  label,
  size,
  variant,
  width,
  tooltipContents,
  // Routing
  as = 'div' as Tag,
  to,
  onClick,
  ref,
  ...remaining
}: PolymorphicComponentProps<Tag, BadgeProps>) {
  const controlSize = useControlSize(size, 'medium') as BadgeSize;
  const badgeVariant = useVariant(variant) as VariantProp;
  const badgeEnds = useBadgeEnds(ends, 'card');
  const badgeArrangement = useBadgeArrangement(arrangement, 'leading-label');
  const controlCondense = useControlCondense(condense);
  const isSelected = useSelected();

  const hasLabel = label || children;
  const isHiddenLabel = badgeArrangement === 'hidden-label';
  const condenseQueries =
    icon && !isHiddenLabel && controlCondense !== false ? controlCondense : undefined;
  const measuresLabel = !isHiddenLabel && (elide || condenseQueries !== undefined);

  const labelRef = useRef<HTMLSpanElement>(null);
  const [isLabelClipped, setIsLabelClipped] = useState(false);

  // Measure on hover (not every render) and disable the tooltip until the label is truncated,
  // condensed away, or hidden-label. flushSync commits before Base UI's pointerenter handler.
  const handlePointerEnter = useCallback(() => {
    const el = labelRef.current;
    if (el) {
      flushSync(() => {
        setIsLabelClipped(el.offsetParent === null || el.scrollWidth > el.clientWidth);
      });
    }
  }, []);

  const handlePointerLeave = useCallback(() => {
    setIsLabelClipped(false);
  }, []);

  // Hide the label's `AlignStack` wrapper, not just the label, so no flex gap is left behind.
  const condenseCSS = condenseQueries
    ? atContainers(condenseQueries, {
        [`& ${AlignStackElements}:has(> ${BadgeLabel})`]: { display: 'none' },
      })
    : undefined;

  const badge = (
    <BadgeContainer
      {...remaining}
      css={condenseCSS}
      id={id}
      ref={ref}
      internal={internal}
      as={as}
      to={to}
      onClick={onClick}
      clickable={onClick !== undefined}
      ends={badgeEnds}
      size={controlSize}
      variant={resolveVariant({ variant: badgeVariant, selected: isSelected })}
      style={{ width }}
      elide={elide}
      onPointerEnter={measuresLabel ? handlePointerEnter : undefined}
      onPointerLeave={measuresLabel ? handlePointerLeave : undefined}
    >
      <AlignStack
        align="center"
        justify="center"
        direction={badgeArrangement === 'leading-label' ? 'row' : 'row-reverse'}
        gap={4}
        preset={getBadgePreset(controlSize)}
        end={icon && <BadgeIcon icon={icon} size={controlSize} />}
      >
        {badgeArrangement !== 'hidden-label' && hasLabel && (
          <BadgeLabel
            ref={labelRef}
            fontVariantNumeric="tabular"
            size={size}
            weight="bold"
            whitespace="no-wrap"
            elide={elide}
          >
            {children || label}
          </BadgeLabel>
        )}
      </AlignStack>
    </BadgeContainer>
  );

  const labelAndChildrenDiffer = label && children && label !== children;

  if (isHiddenLabel || measuresLabel || labelAndChildrenDiffer || tooltipContents) {
    const resolvedTooltipContents = tooltipContents ?? label ?? children;
    const canShowTooltip =
      isHiddenLabel || isLabelClipped || tooltipContents != null || labelAndChildrenDiffer;
    return (
      <Tooltip contents={resolvedTooltipContents} disabled={!canShowTooltip}>
        {badge}
      </Tooltip>
    );
  }

  return badge;
}
